import React, { useMemo, useState, useRef } from 'react';
import { Box, Typography } from 'omaha-design-system';
import type { Stint, PlayerRotation } from './types';
import { getStintColor, formatPM, computeOverlaps } from './constants';
import StintHoverCard from './StintHoverCard';

// Hoisted to module scope for stable Emotion references (React.memo optimization)
const stintBarSx = {
  position: 'absolute',
  top: 4,
  bottom: 4,
  borderRadius: '2px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'filter 0.1s, box-shadow 0.1s',
  '&:hover': {
    filter: 'brightness(1.1)',
    boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.3)',
  },
} as const;

const stintLabelSx = {
  fontSize: '0.75rem',
  fontWeight: 700,
  color: 'white',
  textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
} as const;

interface StintBarProps {
  stint: Stint;
  gameDuration: number;
  playerName: string;
  allPlayers: PlayerRotation[];
  opponentPlayers: PlayerRotation[];
  teamAbbrev: string;
  opponentAbbrev: string;
}

const StintBar: React.FC<StintBarProps> = ({ stint, gameDuration, playerName, allPlayers, opponentPlayers, teamAbbrev, opponentAbbrev }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 });
  const barRef = useRef<HTMLDivElement>(null);

  const leftPct = (stint.startTime / gameDuration) * 100;
  const widthPct = ((stint.endTime - stint.startTime) / gameDuration) * 100;
  const showLabel = widthPct > 3;

  const teammates = useMemo(
    () => computeOverlaps(stint, playerName, allPlayers),
    [allPlayers, playerName, stint.startTime, stint.endTime],
  );

  const opponents = useMemo(
    () => computeOverlaps(stint, playerName, opponentPlayers),
    [opponentPlayers, playerName, stint.startTime, stint.endTime],
  );

  const handleMouseEnter = () => {
    if (barRef.current) {
      const rect = barRef.current.getBoundingClientRect();
      setHoverPos({
        x: rect.left + rect.width / 2,
        y: rect.top,
      });
    }
    setIsHovered(true);
  };

  return (
    <>
      <Box
        ref={barRef}
        sx={stintBarSx}
        style={{
          left: `${leftPct}%`,
          width: `${Math.max(widthPct, 0.5)}%`,
          backgroundColor: getStintColor(stint.plusMinus),
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setIsHovered(false)}
      >
        {showLabel && <Typography component="span" sx={stintLabelSx}>{formatPM(stint.plusMinus)}</Typography>}
      </Box>

      {isHovered && <StintHoverCard stint={stint} position={hoverPos} teammates={teammates} opponents={opponents} teamAbbrev={teamAbbrev} opponentAbbrev={opponentAbbrev} />}
    </>
  );
};

export default React.memo(StintBar);
